import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import mixpanel from 'mixpanel-browser'

import { useBuildingContext } from '../../../../contexts/buildingContext'
import { useViewportWidth } from '../../../../hooks/useViewportWidth'
import { BREAKPOINTS } from '../../../../styles/breakpoints'
import {
  applyViewerPositionDelta,
  calculateViewerPositionDelta,
  reverseViewerPositionDelta,
} from '../../../../utils'
import { useImageViewerContext } from '../imageViewerContext'
import { SplitScreenContainer } from '../styles'
import { ViewerPosition } from '../types'

import { SplitScreenGutter } from './SplitScreenGutter'
import { ViewerPane } from './ViewerPane/ViewerPane'

const defaultPosition: ViewerPosition = {
  yaw: 0,
  pitch: 0,
  hfov: 120,
}

const zeroPosition: ViewerPosition = {
  yaw: 0,
  pitch: 0,
  hfov: 0,
}

export const SplitScreenManager = ({
  minDate,
  maxDate,
}: { minDate?: Date; maxDate?: Date } = {}) => {
  const { state: buildingState } = useBuildingContext()
  const { state: imageViewerState, updateImageViewer } = useImageViewerContext()
  const viewportWidth = useViewportWidth()
  const isMobileLayout = viewportWidth < BREAKPOINTS.tablet
  const pendingMasterSync = useRef<boolean>(false)
  const pendingSubordinateSync = useRef<boolean>(false)
  // Under sync + pnlm-global both panes get mousedown/RAF onUpdate. First
  // drag event wins as leader so only one pending*Sync direction runs.
  const dragLeaderRef = useRef<'master' | 'subordinate' | null>(null)
  const [sync, setSync] = useState<boolean>(true)
  const [compass, setCompass] = useState<boolean>(false)
  const [masterPosition, setMasterPosition] =
    useState<ViewerPosition>(defaultPosition)
  const [subordinatePosition, setSubordinatePosition] =
    useState<ViewerPosition>(defaultPosition)
  const [positionDelta, setPositionDelta] =
    useState<ViewerPosition>(zeroPosition)
  const [masterFollow, setMasterFollow] = useState<boolean>(false)
  const [subordinateFollow, setSubordinateFollow] = useState<boolean>(false)

  // Ref-based communication: allows subordinate pane (ForgeViewer) to request
  // a screenshot from the master pane (Pannellum camera image)
  const masterScreenshotFnRef = useRef<
    ((callback: (blob: Blob) => void) => void) | null
  >(null)

  const registerMasterScreenshot = useCallback(
    (fn: (callback: (blob: Blob) => void) => void) => {
      masterScreenshotFnRef.current = fn
    },
    [],
  )

  const requestCameraScreenshot = useCallback(
    (callback: (blob: Blob) => void) => {
      if (masterScreenshotFnRef.current) {
        masterScreenshotFnRef.current(callback)
      }
    },
    [],
  )

  const handleMasterUpdate = useCallback(
    (newValues: ViewerPosition) => {
      if (sync) {
        if (
          dragLeaderRef.current == null ||
          dragLeaderRef.current === 'master'
        ) {
          pendingMasterSync.current = true
        }
      } else {
        setPositionDelta(
          calculateViewerPositionDelta(newValues, subordinatePosition),
        )
      }
      setMasterPosition(newValues)
    },
    [sync, subordinatePosition],
  )

  const handleSubordinateUpdate = useCallback(
    (newValues: ViewerPosition) => {
      if (sync) {
        if (
          dragLeaderRef.current == null ||
          dragLeaderRef.current === 'subordinate'
        ) {
          pendingSubordinateSync.current = true
        }
      } else {
        setPositionDelta(
          calculateViewerPositionDelta(masterPosition, newValues),
        )
      }
      setSubordinatePosition(newValues)
    },
    [masterPosition, sync],
  )

  // Sync-on image↔image: native Pannellum `pnlm-global` already moves both
  // cameras. Driving followRealtime here made both panes followers, dropped the
  // mouseup commit, then lookAt'd stale React pose (snap-back). Keep React
  // follow only for Forge/XRay, which cannot share pannellum mouse sync.
  const handleMasterDragging = useCallback(
    (isActive: boolean) => {
      if (isActive) {
        if (dragLeaderRef.current == null) {
          dragLeaderRef.current = 'master'
        }
        if (sync && imageViewerState.pane2Type === 'forge') {
          setSubordinateFollow(true)
        }
      } else {
        setSubordinateFollow(false)
        if (dragLeaderRef.current === 'master') {
          dragLeaderRef.current = null
        }
      }
    },
    [imageViewerState.pane2Type, sync],
  )

  const handleSubordinateDragging = useCallback(
    (isActive: boolean) => {
      if (isActive) {
        if (dragLeaderRef.current == null) {
          dragLeaderRef.current = 'subordinate'
        }
        if (sync && imageViewerState.pane2Type === 'forge') {
          setMasterFollow(true)
        }
      } else {
        setMasterFollow(false)
        if (dragLeaderRef.current === 'subordinate') {
          dragLeaderRef.current = null
        }
      }
    },
    [imageViewerState.pane2Type, sync],
  )

  const masterSyncedTo = useMemo(() => {
    return sync ? 'pnlm-global' : 'image-viewer-master'
  }, [sync])

  const subordinateSyncedTo = useMemo(() => {
    return sync ? 'pnlm-global' : 'image-viewer-subordinate'
  }, [sync])

  useEffect(() => {
    if (sync) {
      if (compass) {
        setPositionDelta(zeroPosition)
        pendingMasterSync.current = true
      }
    }
  }, [sync, compass])

  useEffect(() => {
    if (pendingMasterSync.current) {
      pendingMasterSync.current = false
      if (compass) {
        setSubordinatePosition(masterPosition)
      } else {
        setSubordinatePosition(
          applyViewerPositionDelta(masterPosition, positionDelta),
        )
      }
    }
  }, [compass, masterPosition, positionDelta])

  useEffect(() => {
    if (pendingSubordinateSync.current) {
      pendingSubordinateSync.current = false
      if (compass) {
        setMasterPosition(subordinatePosition)
      } else {
        setMasterPosition(
          reverseViewerPositionDelta(subordinatePosition, positionDelta),
        )
      }
    }
  }, [compass, positionDelta, subordinatePosition])

  /* Hide Tour hotspots while in split screen, but don't delete them */
  useEffect(() => {
    if (imageViewerState.splitScreen) {
      const hss = Array.from(document.getElementsByClassName('pnlm-TOUR'))
      hss.forEach(hs => ((hs as HTMLElement).style.display = 'none'))
      mixpanel.track('View Split Screen')
    } else {
      const hss = Array.from(document.getElementsByClassName('pnlm-TOUR'))
      hss.forEach(hs => ((hs as HTMLElement).style.display = 'block'))
      updateImageViewer({ pane2Type: 'image' })
    }
  }, [
    buildingState.tourPoints,
    imageViewerState.splitScreen,
    updateImageViewer,
  ])

  useEffect(() => {
    updateImageViewer({ sync: sync })
  }, [sync, updateImageViewer])

  useEffect(() => {
    if (isMobileLayout && imageViewerState.splitScreen) {
      updateImageViewer({ splitScreen: false, pane2Type: 'image' })
    }
  }, [isMobileLayout, imageViewerState.splitScreen, updateImageViewer])

  return (
    <SplitScreenContainer
      id="pnlm-global"
      style={{ height: '100%', width: '100%' }}
    >
      {imageViewerState.splitScreen && !isMobileLayout && (
        <>
          <ViewerPane
            type={'subordinate'}
            isSolo={false}
            viewerPositionDelta={positionDelta}
            viewerPosition={subordinatePosition}
            followRealtime={subordinateFollow}
            onDragging={handleSubordinateDragging}
            onUpdate={handleSubordinateUpdate}
            imageData={imageViewerState.subordinate}
            syncedTo={subordinateSyncedTo}
            minDate={minDate}
            maxDate={maxDate}
            onRequestCameraScreenshot={requestCameraScreenshot}
          />
          <SplitScreenGutter
            sync={sync}
            compass={compass}
            showCompass={false}
            onSyncChange={setSync}
            onCompassChange={setCompass}
          />
        </>
      )}
      <ViewerPane
        type={'master'}
        isSolo={!imageViewerState.splitScreen}
        viewerPosition={masterPosition}
        followRealtime={masterFollow}
        onDragging={handleMasterDragging}
        onUpdate={handleMasterUpdate}
        imageData={imageViewerState.master}
        syncedTo={masterSyncedTo}
        minDate={minDate}
        maxDate={maxDate}
        onRegisterScreenshot={registerMasterScreenshot}
      />
    </SplitScreenContainer>
  )
}
